import Control, { ControlParams } from './control';
import Ship from '../element/ship/ship';
import RTSControls from './rtsControls';

import { CoordsType, getAngleBy2XY } from '../../utils/geometry';
import { Dictionary } from '../../utils/types';
import Game from '../game';


export interface ManualControlsParams extends ControlParams { }

export default class ManualControls extends Control implements ManualControlsParams {
  shooting = false;
  coords!: CoordsType;
  inputs: Dictionary<boolean>;

  constructor(params: ControlParams) {
    super(params);
    this.shooting = false;
    this.inputs = {
      UP: false,
      DOWN: false,
      LEFT: false,
      RIGHT: false,
      SHOOTING: false,
    };
  }

  getPointerCoords(e: MouseEvent, ignoreCamera = false): { x: number, y: number } {
    return RTSControls.prototype.getPointerCoords.apply(this, [e, ignoreCamera]);
  }

  onMouseMove = (e: MouseEvent): void => {
    e.preventDefault();
    this.updateCoords(e);
  };

  onMouseDown = (e: MouseEvent): void => {
    e.preventDefault();
    this.inputs.SHOOTING = true;
  };

  onMouseUp = (e: MouseEvent): void => {
    e.preventDefault();
    this.inputs.SHOOTING = false;
  };

  onMouseWheel = (e: WheelEvent): void => {
    Game.instance.controlSchemes.rts.onMouseWheel(e); // re-use the same zoom function than RtsControls
    this.updateCoords(e);
  };

  onKeyDown = (e: KeyboardEvent): void => {
    if (e.code === 'KeyC') {
      this.disable();
    }
    else if (e.code === 'Space') {
      Game.instance.togglePause();
    }

    const directionByInput = {
      'KeyW': 'UP',
      'KeyA': 'LEFT',
      'KeyS': 'DOWN',
      'KeyX': 'DOWN',
      'KeyD': 'RIGHT',
    } as Dictionary<string>;
    const input = directionByInput[e.code];
    if (input in this.inputs)
      this.inputs[input] = true;
  };

  onKeyUp = (e: KeyboardEvent): void => {
    const directionByInput = {
      'KeyW': 'UP',
      'KeyA': 'LEFT',
      'KeyS': 'DOWN',
      'KeyX': 'DOWN',
      'KeyD': 'RIGHT',
    } as Dictionary<string>;
    const input = directionByInput[e.code];
    if (input in this.inputs)
      this.inputs[input] = false;
  };

  enable(): ManualControls {
    super.enable();

    const selectedElements = Game.instance.controlSchemes.rts.getSelectedElements();
    this.getCamera()!.targetElements = selectedElements;

    selectedElements.forEach(e => {
      e.behaviour?.clearOrders();
    });

    this.coords = { x: 0, y: 0 };
    return this;
  }

  disable(): ManualControls {
    super.disable();
    Game.instance.controlSchemes.rts.enable();
    Game.instance.controlSchemes.rts.selectedElementsIds
      .map(id => this.scene.elements[id] as Ship)
      .forEach(ship => {
        if (ship && ship.behaviour) {
          ship.behaviour.clearOrders();
          ship.stop();
        }
      })
      ;
    this.getCamera()!.targetElements = [];
    return this;
  }

  updateCoords(e: MouseEvent): void {
    const { x, y } = this.getPointerCoords(e, true);
    this.coords!.x = x;
    this.coords!.y = y;
  }

  pilot(element: Ship, modifier: number): void {
    if (!element) return;
    const { x, y } = this.coords;
    const camera = this.getCamera();

    const coordsByElements = this.getCoordsForMultipleElements(
      { x, y },
      Game.instance.controlSchemes.rts.getSelectedElements(),
    );
    const currentElementObjectiveCoords = coordsByElements[element.id];

    let currentLookAngle = element.lookAngle;
    if (element.canStrafe) {
      const angleToReach = getAngleBy2XY({
        x1: element.x ?? 0,
        y1: element.y ?? 0,
        x2: currentElementObjectiveCoords.x + camera!.x - camera!.width / 2,
        y2: currentElementObjectiveCoords.y + camera!.y - camera!.height / 2,
      });
      currentLookAngle = element.rotateTowardsAngle(angleToReach, modifier);
    }

    if (this.inputs.UP) {
      element.accelerate(modifier);
      element.moveAngle = currentLookAngle;
    }
    else if (this.inputs.DOWN) {
      if (element.moveSpeed === 0 && element.canStrafe) {
        element.strafeDown(modifier);
      }
      else {
        element.brake(modifier);
      }
    }
    else
      element.decelerate(modifier);

    if (this.inputs.LEFT) {
      if (element.canStrafe)
        element.strafeLeft(modifier);
      else
        element.rotateTowardsAngle(element.lookAngle ?? 0 + 5, modifier);
    }
    if (this.inputs.RIGHT) {
      if (element.canStrafe)
        element.strafeRight(modifier);
      else
        element.rotateTowardsAngle(element.lookAngle ?? 0 - 5, modifier);
    }
  }
}
